import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import { type ComponentRef, forwardRef, memo, type ReactNode } from 'react';

import { ChevronRightIcon } from '@air/design-icons';
import { type DropdownMenuSubProps, dropdownMenuItem } from '@air/primitive-dropdown-menu';

import {
  agentDropdownMenuItem,
  agentDropdownMenuSubPanel,
} from '~/components/v2/components/private-selection-bar/agent-selection-bar/agent-dropdown-menu/agentDropdownMenuVariants';

export type AgentDropdownMenuSubProps = DropdownMenuSubProps & {
  children: ReactNode;
};

/**
 * Agent-styled submenu — applies dark item chrome on SubTrigger and dark glass on portaled SubContent.
 * Mirrors `@air/primitive-dropdown-menu` `DropdownMenuSub` so Radix keyboard/focus behaviour is unchanged.
 */
export const AgentDropdownMenuSub = memo(
  forwardRef<ComponentRef<typeof RadixDropdownMenu.SubContent>, AgentDropdownMenuSubProps>(
    (
      {
        children,
        className,
        collisionPadding = 8,
        defaultOpen,
        description,
        disabled,
        label,
        onOpenChange,
        open,
        prefix,
        size = 'small',
        suffix,
        'data-id': dataId,
        'data-intercomid': dataIntercomId,
        ...restOfProps
      },
      forwardedRef,
    ) => {
      const hasDescription = !!description;
      const isSmall = size === 'small';

      return (
        <RadixDropdownMenu.Sub defaultOpen={defaultOpen} onOpenChange={onOpenChange} open={open}>
          <RadixDropdownMenu.SubTrigger
            className={dropdownMenuItem({
              className: agentDropdownMenuItem(),
              size,
            })}
            data-id={dataId}
            data-intercomid={dataIntercomId}
            data-testid="AGENT_SELECTION_BAR_DROPDOWN_MENU_SUB_TRIGGER"
            disabled={disabled}
          >
            {prefix ? (
              <div
                className={classNames(
                  'mr-2 flex shrink-0 items-center gap-1',
                  hasDescription && isSmall && 'mt-0.5',
                  hasDescription && !isSmall && 'mt-1',
                  !hasDescription && 'mt-0',
                )}
              >
                {prefix}
              </div>
            ) : null}

            <div className="flex min-w-0 grow flex-col">
              <span className={classNames('truncate text-white/90', isSmall ? 'text-14' : 'text-16')}>{label}</span>
              {hasDescription ? (
                <span className={classNames('text-white/65', isSmall ? 'text-12' : 'text-14')}>{description}</span>
              ) : null}
            </div>

            <div
              className={classNames(
                'ml-2 flex shrink-0 items-center gap-1',
                hasDescription && isSmall && 'mt-0.5',
                hasDescription && !isSmall && 'mt-1',
                !hasDescription && 'mt-0',
              )}
            >
              {suffix}
              <ChevronRightIcon size="sm" className="text-current" />
            </div>
          </RadixDropdownMenu.SubTrigger>

          <RadixDropdownMenu.Portal>
            <RadixDropdownMenu.SubContent
              className={agentDropdownMenuSubPanel({ className })}
              collisionPadding={collisionPadding}
              data-testid="AGENT_SELECTION_BAR_DROPDOWN_MENU_SUB"
              data-theme="dark"
              ref={forwardedRef}
              {...restOfProps}
            >
              {children}
            </RadixDropdownMenu.SubContent>
          </RadixDropdownMenu.Portal>
        </RadixDropdownMenu.Sub>
      );
    },
  ),
);

AgentDropdownMenuSub.displayName = 'AgentDropdownMenuSub';
